package top.wcpe.allincore.layout.engine.internal.taffy

import org.khronos.webgl.get
import top.wcpe.allincore.layout.engine.AvailableSpace
import top.wcpe.allincore.layout.engine.Box
import top.wcpe.allincore.layout.engine.Dimension
import top.wcpe.allincore.layout.engine.Edges
import top.wcpe.allincore.layout.engine.LayoutEngine
import top.wcpe.allincore.layout.engine.LayoutMode
import top.wcpe.allincore.layout.engine.LayoutNode
import top.wcpe.allincore.layout.engine.LayoutResult
import top.wcpe.allincore.layout.engine.LayoutStyle

/**
 * 基于官方 Rust taffy（wasm）的 [LayoutEngine] JS 实现（ADR-0069 决策 2）。
 *
 * 树遍历语义与 JVM `TaffyLayoutEngine` 逐行对应：visible=false 跳过整棵子树、
 * detached 子树主树算完后独立计算（嵌套 detached 同 JVM 丢弃）、flex 父下的透明
 * 布局容器（display: contents）摊平后按子包围范围合成盒。样式经 JSON 协议交
 * wasm 侧翻译（taffy-wasm/src/lib.rs，对齐 StyleTranslator 逐字段语义）。
 *
 * D1 性能埋点暂不接（JVM 侧 UiPerfSink 依赖 System.nanoTime；Web 端观测按需另接，不影响布局语义）。
 */
internal class WasmTaffyLayoutEngine : LayoutEngine {

    override fun compute(root: LayoutNode, space: AvailableSpace): LayoutResult {
        val boxes = HashMap<LayoutNode, Box>()
        // 收集"从父容器布局分离"的子树根（如 visible=true 的 modal）：主树算完后逐个独立计算。
        val detachedRoots = mutableListOf<LayoutNode>()
        // 被摊平的透明布局容器（display: contents）：布局后据子包围范围合成盒。
        val flattenedTransparent = mutableListOf<LayoutNode>()

        computeTree(root, space, boxes, detachedRoots, flattenedTransparent)
        synthesizeTransparentBoxes(flattenedTransparent, boxes)
        // 单独计算分离子树并合并；嵌套分离子树传丢弃列表（与 JVM 行为一致：内层 detached 不布局）。
        for (detached in detachedRoots) {
            val subFlattened = mutableListOf<LayoutNode>()
            computeTree(detached, space, boxes, mutableListOf(), subFlattened)
            synthesizeTransparentBoxes(subFlattened, boxes)
        }
        return MapResult(boxes)
    }

    /** 一棵（子）树的完整计算：walk 收集节点 → 序列化 → wasm 计算 → 回填 boxes。 */
    private fun computeTree(
        root: LayoutNode,
        space: AvailableSpace,
        boxes: MutableMap<LayoutNode, Box>,
        detachedRoots: MutableList<LayoutNode>,
        flattenedTransparent: MutableList<LayoutNode>,
    ) {
        val entries = mutableListOf<Entry>()
        buildSpec(root, entries, detachedRoots, flattenedTransparent)
        val out = computeLayoutJson(buildRequestJson(space, entries)) { idx, kw, kh ->
            // 仅 measure=true 的叶子会被回调；r 为 null / 长度不足按 NaN 处理（对齐 JVM toTaffyMeasure）
            val r = entries[idx].node.measurer()?.measure(kw.toFloat(), kh.toFloat())
            floatArrayOf(
                if (r != null && r.size >= 1) r[0] else Float.NaN,
                if (r != null && r.size >= 2) r[1] else Float.NaN,
            )
        }
        for ((i, e) in entries.withIndex()) {
            boxes[e.node] = Box(out[i * 4], out[i * 4 + 1], out[i * 4 + 2], out[i * 4 + 3])
        }
    }

    /** 协议节点：node + 是否 measure 叶子 + 子节点下标（walk 期间回填）。 */
    private class Entry(val node: LayoutNode, val measure: Boolean) {
        val children = mutableListOf<Int>()
    }

    /** 对齐 JVM buildTree：仅无子叶子取 measurer；先登记自身、再挂子（自身下标 = 登记时列表长度）。 */
    private fun buildSpec(
        node: LayoutNode,
        entries: MutableList<Entry>,
        detachedRoots: MutableList<LayoutNode>,
        flattenedTransparent: MutableList<LayoutNode>,
    ): Int {
        val measurer = if (node.children().isEmpty()) node.measurer() else null
        val idx = entries.size
        val entry = Entry(node, measurer != null)
        entries.add(entry)
        // 摊平前提：父是 flex（ROW/COLUMN）且允许摊平透明子（滚动容器返回 false 保护 flexShrink=0）
        val flexParent = isFlexContainer(node.layoutStyle().mode) && node.flattensTransparentChildren()
        appendChildren(node, entry, flexParent, entries, detachedRoots, flattenedTransparent)
        return idx
    }

    /** 对齐 JVM appendChildren：跳过不可见、剥离 detached、flex 父下摊平透明容器（穿透嵌套）。 */
    private fun appendChildren(
        node: LayoutNode,
        parent: Entry,
        flexParent: Boolean,
        entries: MutableList<Entry>,
        detachedRoots: MutableList<LayoutNode>,
        flattenedTransparent: MutableList<LayoutNode>,
    ) {
        // 子列表快照：与 JVM 同策略，防数据驱动容器并发改 children
        for (child in ArrayList(node.children())) {
            if (!child.visible()) continue
            if (child.detachedFromParentLayout()) {
                detachedRoots.add(child)
                continue
            }
            if (flexParent && child.transparentLayout()) {
                flattenedTransparent.add(child)
                appendChildren(child, parent, flexParent, entries, detachedRoots, flattenedTransparent)
            } else {
                parent.children.add(buildSpec(child, entries, detachedRoots, flattenedTransparent))
            }
        }
    }

    /** 对齐 JVM synthesizeTransparentBoxes：逆序合成，偏移恒 (0,0)、尺寸取子包围范围。 */
    private fun synthesizeTransparentBoxes(
        flattened: List<LayoutNode>,
        boxes: MutableMap<LayoutNode, Box>,
    ) {
        for (i in flattened.indices.reversed()) {
            val t = flattened[i]
            var maxRight = 0f
            var maxBottom = 0f
            for (child in ArrayList(t.children())) {
                if (!child.visible() || child.detachedFromParentLayout()) continue
                val cb = boxes[child] ?: continue
                maxRight = maxOf(maxRight, cb.x() + cb.width())
                maxBottom = maxOf(maxBottom, cb.y() + cb.height())
            }
            boxes[t] = Box(0f, 0f, maxRight, maxBottom)
        }
    }

    private fun isFlexContainer(mode: LayoutMode): Boolean =
        mode == LayoutMode.ROW || mode == LayoutMode.COLUMN

    // ====== 协议序列化（与 taffy-wasm ComputeRequest 一一对应；非有限值在此清洗，JSON 不携带 NaN） ======

    private fun buildRequestJson(space: AvailableSpace, entries: List<Entry>): String {
        val sb = StringBuilder()
        sb.append('{')
        when (space.type()) {
            AvailableSpace.Type.MIN_CONTENT -> sb.append("\"availType\":\"minContent\"")
            AvailableSpace.Type.MAX_CONTENT -> sb.append("\"availType\":\"maxContent\"")
            else -> sb.append("\"availType\":\"definite\",\"availW\":").append(finiteOr(space.width(), 0f))
                .append(",\"availH\":").append(finiteOr(space.height(), 0f))
        }
        sb.append(",\"nodes\":[")
        for ((i, e) in entries.withIndex()) {
            if (i > 0) sb.append(',')
            appendNode(sb, e)
        }
        sb.append("]}")
        return sb.toString()
    }

    private fun appendNode(sb: StringBuilder, e: Entry) {
        val s = e.node.layoutStyle()
        sb.append("{\"mode\":\"").append(s.mode.name).append('"')
        appendDim(sb, "width", s.width)
        appendDim(sb, "height", s.height)
        appendDim(sb, "minWidth", s.minWidth)
        appendDim(sb, "minHeight", s.minHeight)
        appendDim(sb, "maxWidth", s.maxWidth)
        appendDim(sb, "maxHeight", s.maxHeight)
        appendEdges(sb, "margin", s.margin)
        appendEdges(sb, "padding", s.padding)
        sb.append(",\"gap\":").append(finiteOr(s.gap, 0f))
        sb.append(",\"align\":\"").append(s.align.name).append('"')
        sb.append(",\"justify\":\"").append(s.justify.name).append('"')
        // 非有限 grow/shrink 降级 0（对齐 StyleTranslator：shrink 兜底是 0 不是声明默认 1）
        sb.append(",\"flexGrow\":").append(finiteOr(s.flexGrow, 0f))
        sb.append(",\"flexShrink\":").append(finiteOr(s.flexShrink, 0f))
        appendDim(sb, "flexBasis", s.flexBasis)
        sb.append(",\"positionMode\":\"").append(s.positionMode.name).append('"')
        appendDim(sb, "x", s.x)
        appendDim(sb, "y", s.y)
        sb.append(",\"flexWrap\":\"").append(s.flexWrap.name).append('"')
        sb.append(",\"gridColumns\":").append(s.gridColumns)
        sb.append(",\"gridRows\":").append(s.gridRows)
        appendStr(sb, "gridTemplateColumns", s.gridTemplateColumns)
        appendStr(sb, "gridTemplateRows", s.gridTemplateRows)
        appendStr(sb, "gridColumn", s.gridColumn)
        appendStr(sb, "gridRow", s.gridRow)
        appendStr(sb, "gridAutoFlow", s.gridAutoFlow)
        appendStr(sb, "justifyItems", s.justifyItems?.name)
        // 宽高比：NaN = 未声明，省略（对齐 StyleTranslator 仅显式声明时设）
        if (!s.aspectRatio.isNaN() && s.aspectRatio.isFinite()) {
            sb.append(",\"aspectRatio\":").append(s.aspectRatio)
        }
        sb.append(",\"children\":[")
        for ((i, c) in e.children.withIndex()) {
            if (i > 0) sb.append(',')
            sb.append(c)
        }
        sb.append("],\"measure\":").append(e.measure).append('}')
    }

    /** Dimension → {"t":"px|pct|auto","v":n}；非有限 px/pct 降级 auto（对齐 finiteOrAuto）。 */
    private fun appendDim(sb: StringBuilder, name: String, d: Dimension) {
        sb.append(",\"").append(name).append("\":")
        when {
            d is Dimension.Px && d.value().isFinite() ->
                sb.append("{\"t\":\"px\",\"v\":").append(d.value()).append('}')
            d is Dimension.Percent && d.value().isFinite() ->
                sb.append("{\"t\":\"pct\",\"v\":").append(d.value()).append('}')
            else -> sb.append("{\"t\":\"auto\"}")
        }
    }

    /** Edges → [左,右,上,下]；非有限边值降级 0（对齐 toMarginRect/toPaddingRect）。 */
    private fun appendEdges(sb: StringBuilder, name: String, e: Edges) {
        sb.append(",\"").append(name).append("\":[")
            .append(finiteOr(e.left(), 0f)).append(',')
            .append(finiteOr(e.right(), 0f)).append(',')
            .append(finiteOr(e.top(), 0f)).append(',')
            .append(finiteOr(e.bottom(), 0f)).append(']')
    }

    /** 可空字符串字段：null 省略（serde 默认 None）；值经 JSON 转义。 */
    private fun appendStr(sb: StringBuilder, name: String, v: String?) {
        if (v == null) return
        sb.append(",\"").append(name).append("\":\"")
        for (c in v) {
            when {
                c == '"' -> sb.append("\\\"")
                c == '\\' -> sb.append("\\\\")
                c.code < 0x20 -> sb.append("\\u").append(c.code.toString(16).padStart(4, '0'))
                else -> sb.append(c)
            }
        }
        sb.append('"')
    }

    private fun finiteOr(v: Float, fallback: Float): Float = if (v.isFinite()) v else fallback

    /** 基于 Map 的不可变结果视图（对齐 JVM ImmutableResult 的 boxOf 契约）。 */
    private class MapResult(private val boxes: Map<LayoutNode, Box>) : LayoutResult {
        override fun boxOf(node: LayoutNode): Box? = boxes[node]
    }
}
